/**
 * Section paramètres heures supplémentaires
 * @module components/settings/HeuresSupSettings
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Clock, Loader2, Save, Plus, Trash2, Moon, Calendar, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { HeuresSupSettings as HeuresSupSettingsType, TrancheHeureSup, ModeCalculHeuresSup } from '@/types/heures-sup.types';

interface HeuresSupSettingsProps {
  settings: HeuresSupSettingsType;
  onSave: (settings: Partial<HeuresSupSettingsType>) => Promise<void>;
  isLoading?: boolean;
}

export default function HeuresSupSettings({
  settings,
  onSave,
  isLoading = false,
}: HeuresSupSettingsProps) {
  // État local pour les modifications
  const [localSettings, setLocalSettings] = useState<HeuresSupSettingsType>(settings);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Synchroniser avec les props
  useEffect(() => {
    setLocalSettings(settings);
    setHasChanges(false);
  }, [settings]);

  // Mettre à jour un setting
  const updateSetting = <K extends keyof HeuresSupSettingsType>(
    key: K,
    value: HeuresSupSettingsType[K]
  ) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // Gérer les tranches
  const addTranche = () => {
    const lastTranche = localSettings.heures_sup_tranches[localSettings.heures_sup_tranches.length - 1];
    const newTranche: TrancheHeureSup = {
      de: lastTranche ? lastTranche.a + 1 : 36,
      a: 999,
      majoration: 50,
    };
    updateSetting('heures_sup_tranches', [...localSettings.heures_sup_tranches, newTranche]);
  };

  const removeTranche = (index: number) => {
    if (localSettings.heures_sup_tranches.length <= 1) return;
    const newTranches = localSettings.heures_sup_tranches.filter((_, i) => i !== index);
    updateSetting('heures_sup_tranches', newTranches);
  };

  const updateTranche = (index: number, field: keyof TrancheHeureSup, value: number) => {
    const newTranches = [...localSettings.heures_sup_tranches];
    newTranches[index] = { ...newTranches[index], [field]: value };
    updateSetting('heures_sup_tranches', newTranches);
  };

  // Sauvegarder
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(localSettings);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/10 rounded-lg">
            <Clock className="w-5 h-5 text-amber-500" />
          </div>
          <div className="flex-1">
            <CardTitle>Heures supplémentaires</CardTitle>
            <CardDescription>
              Configurez le suivi des heures supplémentaires
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Toggle activation */}
        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
          <div className="space-y-1">
            <Label htmlFor="heures-sup-actives" className="text-base font-medium">
              Activer le suivi des heures supplémentaires
            </Label>
            <p className="text-sm text-muted-foreground">
              Permet de saisir et calculer les coûts des heures supplémentaires sur vos chantiers
            </p>
          </div>
          <Switch
            id="heures-sup-actives"
            checked={localSettings.heures_sup_actives}
            onCheckedChange={(checked) => updateSetting('heures_sup_actives', checked)}
          />
        </div>

        {/* Options visibles uniquement si activé */}
        {localSettings.heures_sup_actives && (
          <div className="space-y-6 animate-fade-up">
            {/* Base horaire */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="base-journaliere">Base horaire journalière</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-4 h-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Au-delà de cette durée quotidienne, les heures sont considérées comme supplémentaires</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="base-journaliere"
                  type="number"
                  step="0.5"
                  min="1"
                  max="12"
                  value={localSettings.heures_sup_base_journaliere}
                  onChange={(e) => updateSetting('heures_sup_base_journaliere', parseFloat(e.target.value) || 8)}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">heures/jour</span>
              </div>
            </div>

            {/* Mode de calcul */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Mode de calcul des majorations</Label>
              <RadioGroup
                value={localSettings.heures_sup_mode}
                onValueChange={(value) => updateSetting('heures_sup_mode', value as ModeCalculHeuresSup)}
              >
                {/* Option Standard */}
                <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                  <RadioGroupItem value="standard" id="mode-standard" className="mt-1" />
                  <div className="space-y-2 flex-1">
                    <Label htmlFor="mode-standard" className="font-medium cursor-pointer">
                      Standard (Code du Travail français)
                    </Label>
                    <div className="text-sm text-muted-foreground space-y-1 bg-muted/50 p-3 rounded-md">
                      <p>• De la 36ème à la 43ème heure/semaine : <span className="font-medium text-amber-500">+25%</span></p>
                      <p>• À partir de la 44ème heure/semaine : <span className="font-medium text-amber-500">+50%</span></p>
                    </div>
                  </div>
                </div>

                {/* Option Personnalisé */}
                <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                  <RadioGroupItem value="personnalise" id="mode-personnalise" className="mt-1" />
                  <div className="space-y-3 flex-1">
                    <Label htmlFor="mode-personnalise" className="font-medium cursor-pointer">
                      Personnalisé (convention collective)
                    </Label>

                    {localSettings.heures_sup_mode === 'personnalise' && (
                      <div className="space-y-3">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Tranche</TableHead>
                              <TableHead>De (h/sem)</TableHead>
                              <TableHead>À (h/sem)</TableHead>
                              <TableHead>Majoration (%)</TableHead>
                              <TableHead className="w-12"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {localSettings.heures_sup_tranches.map((tranche, index) => (
                              <TableRow key={index}>
                                <TableCell className="font-medium">{index + 1}</TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={tranche.de}
                                    onChange={(e) => updateTranche(index, 'de', parseInt(e.target.value) || 0)}
                                    className="w-20"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={tranche.a >= 999 ? '' : tranche.a}
                                    placeholder="∞"
                                    onChange={(e) => updateTranche(index, 'a', parseInt(e.target.value) || 999)}
                                    className="w-20"
                                  />
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Input
                                      type="number"
                                      min="0"
                                      max="200"
                                      value={tranche.majoration}
                                      onChange={(e) => updateTranche(index, 'majoration', parseInt(e.target.value) || 0)}
                                      className="w-20"
                                    />
                                    <span className="text-muted-foreground">%</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeTranche(index)}
                                    disabled={localSettings.heures_sup_tranches.length <= 1}
                                    className="text-muted-foreground hover:text-destructive"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <Button variant="outline" size="sm" onClick={addTranche}>
                          <Plus className="w-4 h-4 mr-2" />
                          Ajouter une tranche
                        </Button>
                        <p className="text-xs text-muted-foreground">
                          Définissez vos propres tranches de majoration selon votre convention collective
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Options supplémentaires */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Options supplémentaires</Label>

              {/* Heures de nuit */}
              <div className="p-4 border rounded-lg space-y-4">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="nuit-actives"
                    checked={localSettings.heures_sup_nuit_actives}
                    onCheckedChange={(checked) => updateSetting('heures_sup_nuit_actives', !!checked)}
                  />
                  <div className="flex items-center gap-2">
                    <Moon className="w-4 h-4 text-muted-foreground" />
                    <Label htmlFor="nuit-actives" className="cursor-pointer font-medium">
                      Activer les heures de nuit
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">Applique une majoration spécifique pour le travail entre les heures définies.</p>
                </div>

                {localSettings.heures_sup_nuit_actives && (
                  <div className="grid grid-cols-3 gap-4 pl-7 animate-fade-up">
                    <div className="space-y-2">
                      <Label htmlFor="nuit-majoration">Majoration</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          id="nuit-majoration"
                          type="number"
                          min="0"
                          max="200"
                          value={localSettings.heures_sup_nuit_majoration}
                          onChange={(e) => updateSetting('heures_sup_nuit_majoration', parseInt(e.target.value) || 0)}
                          className="w-20"
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nuit-debut">De</Label>
                      <Input
                        id="nuit-debut"
                        type="time"
                        value={localSettings.heures_sup_nuit_debut}
                        onChange={(e) => updateSetting('heures_sup_nuit_debut', e.target.value)}
                        className="w-28"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nuit-fin">À</Label>
                      <Input
                        id="nuit-fin"
                        type="time"
                        value={localSettings.heures_sup_nuit_fin}
                        onChange={(e) => updateSetting('heures_sup_nuit_fin', e.target.value)}
                        className="w-28"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Dimanches et jours fériés */}
              <div className="p-4 border rounded-lg space-y-4">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="ferie-actives"
                    checked={localSettings.heures_sup_ferie_actives}
                    onCheckedChange={(checked) => updateSetting('heures_sup_ferie_actives', !!checked)}
                  />
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <Label htmlFor="ferie-actives" className="cursor-pointer font-medium">
                      Activer les heures dimanche et jours fériés
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">Majoration automatique pour le travail les dimanches et jours fériés.</p>
                </div>

                {localSettings.heures_sup_ferie_actives && (
                  <div className="pl-7 animate-fade-up">
                    <div className="space-y-2">
                      <Label htmlFor="ferie-majoration">Majoration</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          id="ferie-majoration"
                          type="number"
                          min="0"
                          max="200"
                          value={localSettings.heures_sup_ferie_majoration}
                          onChange={(e) => updateSetting('heures_sup_ferie_majoration', parseInt(e.target.value) || 0)}
                          className="w-20"
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bouton Enregistrer */}
            <div className="pt-4 border-t">
              <Button onClick={handleSave} disabled={saving || !hasChanges}>
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Enregistrer les paramètres
              </Button>
              {hasChanges && (
                <span className="ml-3 text-sm text-amber-500">
                  Modifications non enregistrées
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
